import React, {useCallback, useMemo, useState} from 'react'
import {Stack} from '@src/CommonComponents/Stack/Stack.tsx'
import styles from "./checkInPage.module.less"
import {FirstCheckIn} from "@UI/illustrationsSvg/FirstCheckIn.tsx";
import {
    Body_16_Coral_Primary_Regular_16,
    Title_20_Coral_Strong_emphasis_20,
    Title_40_Coral_emphasis_40
} from "@UI/typography/generated/typography.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {getCurrentDateTimeIso,} from "@src/utils/formatDate.ts";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {LogOut} from "@UI/components/icons/LogOut.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {apiClient} from "@src/httpClient/api.ts";
import {
    EmployeeInline,
    WorkEventKindEnum as KindEnum,
    WorkEventCreateInputRequest,
    WorkEventIngestOutput,
    WorkEventOutput
} from "@src/httpClient/__generated__/Api.ts";
import {GeoState, useGpsStore} from "@src/Store/gpsStore.ts";
import {useMutation} from "@tanstack/react-query";
import {queryClient} from "@src/QueryClient/queryClient.ts";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorModalContent} from "@src/CommonComponents/ErrorContent/ErrorContent.tsx";
import {addEventOffline, addEventToOutbox} from "@src/offline/offline.ts";
import {useEventsStore} from "@src/Store/eventsStore.ts";
import {WorkDayInfo} from "@src/CommonComponents/WorkDayInfo/WorkDayInfo.tsx";
import {WorkingAfterCheckIn} from "@UI/illustrationsSvg/WorkingAfterCheckIn.tsx";
import {LogIn} from "@UI/components/icons/LogIn.tsx";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {useModalStore} from "@src/Layers/ModalLayer/ModalStore.ts";
import {AskFinishWork} from "@src/Modals/AskModal.tsx";
import {ForgotClockInModal} from "@src/Modals/ForgotClockInModal.tsx";
import {useModalState} from "@src/Store/modalStore.ts";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {GPSWaitModal} from "@src/Modals/GPSWaitModal.tsx";
import {GPSSuccessModal} from "@src/Modals/GPSSuccessModal.tsx";
import {GPSFailModal} from "@src/Modals/GPSFailModal.tsx";
import {Header} from "@src/Features/MainLayout/Components/Header/Header.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {v4 as uuid} from "uuid";
import {useNet} from "@src/utils/net.ts";
import {CheckInOutSkeleton} from "@src/Pages/Views/Employee/CheckInOut/skeleton/CheckInOutSkeleton.tsx";
import {StatusBanner} from "@src/CommonComponents/StatusBanner/StatusBanner.tsx";
import cn from "classnames";
import {parseEmployeeStatus} from "@src/utils/workStatusParser.ts";
import {EmployeeStatus} from "@src/CommonComponents/WorkStatus/WorkStatus.tsx";
import {sleep} from "@src/offline/sync.ts";
import {Clock} from "@src/CommonComponents/Clock/Clock.tsx";
import {Modal} from "@src/CommonComponents/Modal/Modal.tsx";
import {EventsList} from "@src/CommonComponents/EventsHistoryElement/EventsHistoryElement";
import {useWorkDaysStore} from "@src/Store/workDaysStore.ts";
import {GpsStatusButton} from "@src/CommonComponents/GpsStatusButton/GpsStatusButton.tsx";


interface Props {
    createEvent: (v: KindEnum) => void
    loading?: boolean
}

export const CheckOutView: React.FC<Props> = ({createEvent, loading}) => {
    const gpsState = useGpsStore(x => x.state)
    const gpsBlocked = [GeoState.denied, GeoState.unsupported].includes(gpsState)
    const employee = useCredentialsStore(x => x.employee)
    const setActiveModal = useModalStore(x => x.setActiveModal);
    const resetModal = useModalStore(x => x.reset);
    const checkedDontShowAskModal = useModalState((s) => s.checked);
    const currentWorkDay = useWorkDaysStore(x => x.currentWorkDay)

    const addEvent = useCallback(async (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        createEvent(KindEnum.Out)
    }, [])

    const finishAction = useCallback(async (e: any) => {
        if (!currentWorkDay) {
            await addEvent(e)
            return
        }

        if (!checkedDontShowAskModal) {
            setActiveModal(<AskFinishWork executeAction={async () => {
                resetModal()
                await addEvent(e)
            }} cancelHandler={resetModal}/>)
            return
        }
        await addEvent(e)
    }, [currentWorkDay])

    return (
        <Stack direction={"column"} gap={16} className={styles.root}>
            <Stack justifyContent={"space-around"} alignItems={"center"} gap={14}
                   className={cn(styles.whiteBoard, styles.greetingsBlock)}
                   style={{flexWrap: "wrap"}}>
                <Stack className={styles.img} justifyContent={"center"} alignItems={"center"}>
                    <WorkingAfterCheckIn/>
                </Stack>

                <Stack direction={"column"} alignItems={"center"} gap={14} className={styles.greetings}>
                    <Stack direction={"column"} alignItems={"center"} gap={4}>
                        <span className={styles.greetingsText}>
                            <div>
                                <Title_20_Coral_Strong_emphasis_20>
                                    <Gt>Thank you for your hard work,</Gt>
                                    <span
                                        className={styles.userName}>{employee?.last_name} {employee?.first_name}</span>
                                </Title_20_Coral_Strong_emphasis_20>
                            </div>

                            <Title_20_Coral_Strong_emphasis_20>
                                <Gt>Please keep up the good work!</Gt>
                            </Title_20_Coral_Strong_emphasis_20>
                        </span>
                    </Stack>
                    <Stack className={styles.time} alignItems={"center"}>
                        <Title_40_Coral_emphasis_40>
                            <Clock/>
                        </Title_40_Coral_emphasis_40>
                    </Stack>
                    {gpsBlocked &&
                        <GpsStatusButton ignoreMobileScheme/>
                    }

                    <Button icon={<LogIn color={Colors.Obsidian_Light_0}/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_SKY}
                            className={styles.startButton}
                            onClick={finishAction}
                        disabled={gpsBlocked || loading}
                    >
                        <Gt>Finish work</Gt>
                    </Button>

                </Stack>
            </Stack>
            <EventsList workDay={currentWorkDay}/>
        </Stack>
    )
}

export const CheckInView: React.FC<Props> = ({createEvent, loading}) => {
    const gpsState = useGpsStore(x => x.state)
    const gpsBlocked = [GeoState.denied, GeoState.unsupported].includes(gpsState)
    const employee = useCredentialsStore(x => x.employee)
    const currentWorkDay = useWorkDaysStore(x => x.currentWorkDay)
    const events = useEventsStore(x => x.groupedEvents)
    const setActiveModal = useModalStore(x => x.setActiveModal);
    const resetModal = useModalStore(x => x.reset);

    const addEvent = useCallback(async (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        createEvent(KindEnum.In)
    }, [])

    const showForgotClockIn = useCallback(() => {
        setActiveModal(<ForgotClockInModal onClose={resetModal} onClockOut={() => createEvent(KindEnum.Out)}/>)
    }, [])

    return (
        <Stack direction={"column"} gap={16} className={styles.root}>
            <Stack justifyContent={"space-around"} alignItems={"center"} gap={14}
                   className={cn(styles.whiteBoard, styles.greetingsBlock)}
                   style={{flexWrap: "wrap"}}>
                <Stack className={styles.img} justifyContent={"center"} alignItems={"center"}>
                    <FirstCheckIn/>
                </Stack>
                <Stack direction={"column"} alignItems={"center"} gap={14} className={styles.greetings}>
                    <Stack direction={"column"} alignItems={"center"} gap={4}>
                        <span className={styles.greetingsText}>
                            <div>
                                <Title_20_Coral_Strong_emphasis_20>
                                    <Gt>Good morning,</Gt>
                                    <span
                                        className={styles.userName}>{employee?.last_name} {employee?.first_name}</span>
                                </Title_20_Coral_Strong_emphasis_20>
                            </div>

                            <Title_20_Coral_Strong_emphasis_20>
                                <Gt>Let's do our best today too!</Gt>
                            </Title_20_Coral_Strong_emphasis_20>
                        </span>
                    </Stack>

                    <Stack className={styles.time} alignItems={"center"}>
                        <Title_40_Coral_emphasis_40>
                            <Clock/>
                        </Title_40_Coral_emphasis_40>
                    </Stack>
                    {gpsBlocked &&
                        <GpsStatusButton ignoreMobileScheme/>
                    }

                    <Button icon={<LogOut/>} type={ButtonType.secondary}
                            color={ColorNames.CORAL_GREEN}
                            className={styles.startButton}
                            onClick={addEvent}
                            disabled={gpsBlocked || loading}
                    >
                        <Gt>Start work</Gt>
                    </Button>

                    <button type={"button"} className={styles.forgotClockInButton}
                            onClick={showForgotClockIn}
                            disabled={gpsBlocked || loading}>
                        <Body_16_Coral_Primary_Regular_16>
                            <Gt>Forgot to clock in?</Gt>
                        </Body_16_Coral_Primary_Regular_16>
                    </button>
                </Stack>
            </Stack>
            <EventsList workDay={currentWorkDay}/>
            <WorkDayInfo workDay={currentWorkDay} events={events}/>
        </Stack>
    )
}

type ModalType = "wait" | "success" | "error" | null

export const CheckInOutPage = () => {
    const employee = useCredentialsStore(x => x.employee)
    const isOnline = useNet(x => x.online)

    const [showModal, setShowModal] = useState<ModalType>(null)
    const [preparedPayload, setPreparedPayload] = useState<WorkEventOutput | undefined>(undefined)

    const setError = useErrorStore(x => x.setAppError)

    const refetchEvents = useEventsStore(x => x.refetch)
    // After a stamp: the events, and the workday whose status tas recalculated
    const refetchAfterStamp = () => {
        if (refetchEvents) refetchEvents()
        queryClient.invalidateQueries({queryKey: ["workdays_employees"]})
    }
    const employeeGroupedEvents = useEventsStore(x => x.groupedEvents)
    const status = useMemo(() => parseEmployeeStatus(employeeGroupedEvents || []), [employeeGroupedEvents]);

    const [long, setLong] = useState<number | undefined>(0)
    const [lang, setLang] = useState<number | undefined>(0)
    const [branchName, setBranchName] = useState<string | undefined>(undefined)

    const {mutate, isPending, error, variables} = useMutation<
        AxiosResponse<WorkEventIngestOutput>,
        AxiosError,
        WorkEventCreateInputRequest
    >({
        mutationFn: async formData => {
            await addEventOffline(formData as WorkEventOutput, employee as EmployeeInline)
            return await apiClient.api.eventsIngest([formData])
        },
        onError: async (e, variables) => {
            await addEventToOutbox(variables?.idempotency_key)
            sleep(1000).then(() => setShowModal("success"))
            return e
        },
        onSuccess: async (result, variables) => {
            if (result?.data?.skipped?.length > 0 || result.data?.created?.length === 0) {
                await addEventToOutbox(variables?.idempotency_key)
                sleep(1000).then(() =>
                    setError({
                        message: "Failed to send data to server",
                        axiosError: {status: 500, message: "Failed to send data to server"} as AxiosError,
                        showModal: true,
                        retry: async () => mutate(variables)
                    })
                )
                return
            }
            setBranchName(result?.data?.created?.[0]?.branch_name ?? undefined)
            sleep(1000).then(() => setShowModal("success"))
        },
        networkMode: "always",
    })

    const createEvent = useCallback(async (kind: KindEnum) => {
        setShowModal("wait")
        const payload = {
            kind: kind,
            recorded_at: getCurrentDateTimeIso(),
            longitude: null,
            latitude: null,
            idempotency_key: uuid()
        } as WorkEventOutput

        setPreparedPayload(payload)
        sleep(2000).then(() => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        // resetModal()
                        useGpsStore.setState({state: GeoState.granted})
                        payload.longitude = position.coords.longitude
                        payload.latitude = position.coords.latitude
                        setLong(position.coords.longitude)
                        setLang(position.coords.latitude)

                        setPreparedPayload(payload)
                        mutate(payload)
                    },
                    (error) => {
                        console.error(error)
                        if (error.code === error.PERMISSION_DENIED) useGpsStore.setState({state: GeoState.denied})
                        sleep(1000).then(() =>
                            setShowModal("error")
                        )
                    },
                    {enableHighAccuracy: true, timeout: 15000, maximumAge: 0}, // есть таймаут!
                );
            }
        )
    }, [])

    // wrongData = last pair is an out without an in (退勤 stamped after a missed 出勤): not at work
    const kindType = useMemo(() => {
        return [EmployeeStatus.clockedOut, EmployeeStatus.notAtWork, EmployeeStatus.wrongData].includes(status)
            ? KindEnum.In
            : KindEnum.Out
    }, [status])

    if (!employee?.id) return <ErrorModalContent loading={isPending} error={error as AxiosError}/>
    if (isOnline && error) return <ErrorModalContent loading={isPending} error={error}
                                                     handle={() => mutate(variables)}/>
    return (
        <>
            {showModal === "wait" && <Modal><GPSWaitModal/></Modal>}
            {showModal === "success" &&
                <Modal onClose={() => {
                    setShowModal(null)
                    refetchAfterStamp()
                }}>
                    <GPSSuccessModal
                        coords={{
                            longitude: long ?? 0,
                            latitude: lang ?? 0,
                        }}
                        placeName={branchName}
                        onClose={() => {
                            setShowModal(null)
                            refetchAfterStamp()
                        }}
                    />
                </Modal>
            }
            {showModal === "error" &&
                <Modal onClose={() => {
                    setShowModal(null)
                    refetchAfterStamp()
                }}>
                    <GPSFailModal skipAction={() => {
                        setShowModal("wait")
                        mutate(preparedPayload as WorkEventCreateInputRequest)
                    }}
                                  retryAction={() => createEvent(kindType)}/>
                </Modal>
            }

            {kindType === KindEnum.In
                ? <CheckInView createEvent={createEvent} loading={isPending}/>
                : <CheckOutView createEvent={createEvent} loading={isPending}/>
            }
        </>
    )
}


export const EmployeeView = () => {
    const isEventsLoading = useEventsStore(x => x.isLoading)
    if (isEventsLoading) return <CheckInOutSkeleton/>
    return (
        <Stack grow direction={"column"} alignItems={"center"}
               justifyContent={"flex-start"} gap={16}>
            <Stack direction={"column"} className={styles.header} gap={0}>
                <Header showFilters={false}/>
                <StatusBanner/>
            </Stack>

            <Stack grow className={styles.content}>
                <CheckInOutPage/>
            </Stack>
        </Stack>
    )
}

